import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"

import { cn } from "@/utils/styles.helpers"

const statusDotVariants = cva("size-[7px] shrink-0 rounded-full", {
	variants: {
		tone: {
			ok: "bg-ok",
			warn: "bg-warn",
			bad: "bg-bad",
			info: "bg-info",
			neutral: "bg-meta",
		},
	},
	defaultVariants: {
		tone: "neutral",
	},
})

/** The tone of a status: ok (done), warn (waiting on us), bad (at risk), info (in progress), neutral. */
export type StatusTone = NonNullable<
	VariantProps<typeof statusDotVariants>["tone"]
>

export interface StatusDotProps extends React.ComponentProps<"span"> {
	tone?: StatusTone
}

/**
 * A normal state: a coloured dot and the label in secondary text. The default
 * way to show a status in tables, lists and headers.
 */
export function StatusDot({
	tone = "neutral",
	className,
	children,
	...props
}: StatusDotProps) {
	return (
		<span
			data-slot="status-dot"
			className={cn(
				"inline-flex items-center gap-[7px] whitespace-nowrap align-middle text-muted-foreground",
				className,
			)}
			{...props}
		>
			<span aria-hidden className={statusDotVariants({ tone })} />
			{children}
		</span>
	)
}

const statusTagVariants = cva(
	"inline-flex h-5 items-center whitespace-nowrap rounded-[4px] px-[7px] text-[11.5px] leading-5 font-medium",
	{
		variants: {
			tone: {
				ok: "bg-ok-soft text-ok",
				warn: "bg-warn-soft text-warn",
				bad: "bg-bad-soft text-bad",
				info: "bg-info-soft text-info",
				neutral: "bg-foreground/8 text-muted-foreground",
			},
		},
		defaultVariants: {
			tone: "warn",
		},
	},
)

export interface StatusTagProps extends React.ComponentProps<"span"> {
	tone?: StatusTone
}

/**
 * A state that needs someone (Escalated, Critical risk, Overdue, Failed): a
 * soft tinted background with coloured text, no border. Use sparingly; normal
 * states take `StatusDot`.
 */
export function StatusTag({
	tone = "warn",
	className,
	...props
}: StatusTagProps) {
	return (
		<span
			data-slot="status-tag"
			className={cn(statusTagVariants({ tone }), className)}
			{...props}
		/>
	)
}
